
import Vue from 'vue';
import PhotoCamera from '../../Icons/PhotoCamera.vue';
import FileDocument from '../../Icons/DefaultDocument.vue';
import refreshFile from '../../Icons/RefreshFile.vue';
import miniTrashIcon from '../../Icons/MiniTrashIcon.vue';
import { Padder, Spacer as QSpacer } from '../Spacings';
import { Row, Column } from '../Grid';
import QText from '../Text/Text.vue';
import QButton from '../Button/Button.vue';
import Card from '../Card/Card.vue';
import Modal from '../Modal/Modal.vue';
/**
 * Inline uploader component.
 *
 * The uploader file with mini image.
 *
 * The template consist of:
 *
 * - Mini Image
 * - Text
 * - Upload Button / Remove and Reupload icon
 *
 * There is 1 more event:
 *
 * - `error-state`: The validation status; will return <b>true</b> if uploaded image doesn't pass the validation.
 */
export default Vue.extend<any, any, any, any>({
  name: 'InlineUploader',
  components: {
    Padder,
    QSpacer,
    QText,
    QButton,
    Row,
    Column,
    Card,
    Modal,
    PhotoCamera,
    refreshFile,
    miniTrashIcon,
    FileDocument
  },
  props: {
    /**
     * The ref key of the input.
     */
    documentKey: {
      type: String,
      default: ''
    },
    /**
     * The accept file of the input.
     */
    documentType: {
      type: String,
      default: 'image/*'
    },
    /**
     * Loading status of uploading.
     */
    loading: {
      type: Boolean,
      default: false
    },
    /**
     * Placeholder text when the file is empty.
     */
    placeholder: {
      type: String,
      default: 'Placeholder'
    },
    /**
     * Upload text button.
     */
    uploadLabel: {
      type: String,
      default: 'Upload'
    },
    /**
     * Loading text when uploading file.
     */
    loadingHint: {
      type: String,
      default: 'Uploading'
    },
    /**
     * Validation for minimal image height.
     */
    minImageHeight: {
      type: Number,
      default: 0
    },
    /**
     * Validation for minimal image width.
     */
    minImageWidth: {
      type: Number,
      default: 0
    },
    /**
     * Validation for maximal image height.
     */
    maxImageHeight: {
      type: Number,
      default: Number.POSITIVE_INFINITY
    },
    /**
     * Validation for maximal image width.
     */
    maxImageWidth: {
      type: Number,
      default: Number.POSITIVE_INFINITY
    },
    /**
     * The placeholder always shown instead of file name.<br />
     * `true` for fix placeholder, `false` for file name after uploading.
     */
    replacePlaceholder: {
      type: Boolean,
      default: false
    },
    /**
     * Make input file to capture image instead choosing file.
     * Possible value: `camera`
     */
    capture: {
      type: String,
      default: ''
    },
    /**
     * Delete Row Text
     */
    deleteRowText: {
      type: String,
      default: ''
    },
    /**
     * Variation of the Button.
     * Possible value: See **Eucalyptus** > **Button**
     */
    buttonVariant: {
      type: String,
      default: 'outline-primary'
    },
    /**
     * Variation of text inside Button.
     * Possible value: See **Eucalyptus** > **Text**
     */
    textVariant: {
      type: String,
      default: 'secondary'
    },
    /**
     * Replacement Image, used when image is broken.<br />
     */
    replacementImage: {
      type: String,
      default: ''
    },
    /**
     * Show close button inside modal preview <br />
     */
    showCloseButton: {
      type: Boolean,
      default: true
    },
    /**
     * Set color for close button inside modal preview <br />
     */
    closeIconColor: {
      type: String,
      default: ''
    },
    /**
     * Value of the Inline Uploader.<br />
     * Bind in `v-model`.
     */
    value: {
      type: [String, Object],
      default: ''
    },
    /**
     * URL custom image placeholder
     */
    imagePlaceholder: {
      type: String,
      default: ''
    }
  },
  watch: {
    value(value: any) {
      if (value && value.data && value.fileName) {
        if (value.fileType && typeof value.fileType === 'string') {
          this.imageContent = value.fileType.split('/')[0] === 'image' ? value.data : 'file';
        } else {
          this.imageContent = value.fileName.match(this.imageRegex) ? value.data : 'file';
        }
        this.fileName = value.fileName;
      }
    }
  },
  data() {
    return {
      imageContent: '',
      fileName: '',
      isActive: false,
      loadingProcess: false,
      imageRegex: /\.jpeg$|\.jpg$|\.png$|\.gif$|\.webp$|\.tiff$|\.svg$/i,
      loadingDots: 6
    };
  },
  created() {
    this.loadingProcess = this.loading;
  },
  mounted() {
    if (this.value && this.value.fileType) {
      if (this.value.fileType.includes('image')) {
        this.imageContent = this.value.data;
        this.fileName = this.value.fileName;
      } else {
        this.imageContent = 'file';
        this.fileName = this.value.fileName;
      }
    }
  },
  methods: {
    async previewImageDocument(event: Event, key: string) {
      const pointer = this;
      const reader = new FileReader();
      const cvs = document.createElement('canvas');
      const ctx = cvs.getContext('2d');
      const file = this.getFile(event.target);
      if (file) {
        this.loadingProcess = true;
        if (file.fileCategory === 'image') {
          const imageData = event.target as HTMLInputElement;
          let mimeType = file.fileType;
          if (imageData.files && imageData.files[0]) {
            reader.onload = () => {
              const img = new Image();
              img.onload = () => {
                const width = img.naturalWidth;
                const height = img.naturalHeight;
                const highest = Math.max(width, height) === height ? height : width;
                let percent = 0;
                if (highest > 1080) {
                  percent = Math.floor((1080 / highest) * 100) / 100;
                } else {
                  percent = 1;
                }
                if (ctx) {
                  ctx.clearRect(0, 0, cvs.width, cvs.height);
                  cvs.width = Math.floor(width * percent);
                  cvs.height = Math.floor(height * percent);
                  ctx.drawImage(img, 0, 0, cvs.width, cvs.height);
                }
                const newImageData = cvs.toDataURL(mimeType);
                let imageDataSend = {
                  key: pointer.documentKey,
                  fileName: pointer.fileName,
                  data: newImageData,
                  fileType: file.fileType,
                  width,
                  height,
                  fileSize: file.fileSize
                };
                this.loadingProcess = false;
                if (
                  imageDataSend.height <= this.minImageHeight ||
                  imageDataSend.height >= this.maxImageHeight ||
                  imageDataSend.width <= this.minImageWidth ||
                  imageDataSend.width >= this.maxImageWidth
                ) {
                  this.deleteDataFile();
                  // pointer.imageContent = '';
                  // pointer.fileName = '';
                  // pointer.$emit('input', '');
                  pointer.$emit('after-read', null);
                  pointer.$emit('error-state', true);
                } else {
                  pointer.imageContent = newImageData;
                  pointer.fileName = file.name;
                  imageDataSend.fileName = file.name;
                  pointer.$emit('error-state', false);
                  pointer.$emit('input', imageDataSend);
                  pointer.$emit('after-read', imageDataSend);
                }
                pointer.$forceUpdate();
              };
              img.src = reader.result as string;
            };
            mimeType = imageData.files[0].type;
            reader.readAsDataURL(imageData.files[0]);
            (this.$refs[this.documentKey] as any).value = null;
          }
        } else {
          pointer.imageContent = 'file';
          const toBase64 = (file: any) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.readAsDataURL(file);
              reader.onload = () => resolve(reader.result);
              reader.onerror = error => reject(error);
            });
          const fileBase64 = await toBase64(file.file);
          const imageDataSend = {
            key: pointer.documentKey,
            fileName: file.name,
            data: fileBase64,
            fileType: file.fileType,
            fileSize: file.fileSize
          };
          pointer.loadingProcess = false;
          pointer.fileName = file.name;
          pointer.$emit('input', imageDataSend);
          pointer.$emit('after-read', imageDataSend);
          pointer.$forceUpdate();
        }
      }
    },
    handleBrokenImage(event: Event) {
      if (this.replacementImage) {
        this.imageContent = this.replacementImage;
      }
      /**
       * Handle when image is broken
       * @event broken-state
       * @type {function}
       */
      this.$emit('broken-state', event);
    },
    deleteRow() {
      this.$emit('after-delete-row', { key: this.documentKey });
    },
    deleteDataFile() {
      const file = this.$refs[this.documentKey] as any;
      file.value = '';
      this.fileName = '';
      this.imageContent = '';
      /**
       * Handle when user set the value
       * @event input
       * @type {function}
       */
      this.$emit('input', '');
      /**
       * Handle when user delete the image
       * @event after-delete
       * @type {function}
       */
      this.$emit('after-delete', {
        key: this.documentKey
      });
      this.$forceUpdate();
    },
    getFile(file: any) {
      if (file.files[0]) {
        const result = {
          file: file.files[0],
          name: file.files[0].name,
          fileType: file.files[0].type,
          fileExtension: file.files[0].type.split('/')[1],
          fileCategory: file.files[0].type.split('/')[0],
          fileSize: file.files[0].size
        };
        return result;
      }
      return null;
    }
  },
  computed: {
    isLoading() {
      return this.loading || this.loadingProcess;
    }
  }
});
